// navigation-completion Stage C/D — the publishing-QC + intake review control.
//
// A concise, collapsed section in the Navigation tab that surfaces the server's
// navigation-release verdict and offers the TWO INDEPENDENT, accountable approval
// acts:
//   • Intake evidence — approval of the UPLOADED document BEFORE it enters
//     AutoeCTD. It binds to the SELECTED document's SOURCE bytes (the open
//     document's source hash, confirmed by the reviewer); the server supplies the
//     authoritative hash and refuses a mismatch. An attestation is an operator
//     self-attestation; a verifiable record additionally requires Documents:sign
//     and an evidence reference.
//   • Publishing QC — a sign-off on the sealed FINAL package bytes AND the exact
//     compiler package id the reviewer inspected. Requires Documents:sign.
//
// Honesty the UI preserves:
//   • The two gates are INDEPENDENT; neither is implied by the other.
//   • A not-release-eligible verdict is never relabelled eligible, and its
//     blockers are shown verbatim.
//   • Every act shows the EXACT bytes/compile identity it binds to and is an
//     explicit button press — never a silent approve. A reason is mandatory.
//   • Acts operate on the CURRENTLY-LOADED status; a stale status must be
//     refreshed first, and a server 409 asks the reviewer to refresh — no retry.
//

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import {
  navigationRelease,
  type NavigationReleaseStatus,
  type QcRecordWire,
} from '@/api/navigationRelease';
import { navStyles } from './navShared';

export interface NavigationReleaseControlsProps {
  readonly submissionId: string;
  /** The open document — intake binds to this document's SOURCE bytes. */
  readonly documentId: string;
  /** The source hash the reviewer confirms for intake (from the nav envelope). */
  readonly sourceSha256: string;
}

function short(sha: string | null | undefined): string {
  if (!sha) return '—';
  return sha.length > 12 ? `${sha.slice(0, 12)}…` : sha;
}

function statusLabel(s: NavigationReleaseStatus): { text: string; color: string } {
  if (s.status === 'none') return { text: 'No navigation release evidence yet', color: 'var(--text-muted)' };
  if (s.releaseEligible) return { text: 'Release-eligible', color: 'var(--ok, #15803d)' };
  return { text: 'Not release-eligible', color: 'var(--warning, #b45309)' };
}

function kindLabel(r: QcRecordWire): string {
  switch (r.kind) {
    case 'attestation':
      return 'Operator attestation';
    case 'verified-record':
      return 'Verified record';
    case 'qc-signoff':
      return 'Publishing-QC sign-off';
  }
}

export function NavigationReleaseControls({ submissionId, documentId, sourceSha256 }: NavigationReleaseControlsProps): ReactElement {
  const perms = useUserPermissions();
  const canWrite = perms.hasPermission('Documents', 'write');
  const canSign = perms.hasPermission('Documents', 'sign');

  const [status, setStatus] = useState<NavigationReleaseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Intake form
  const [intakeKind, setIntakeKind] = useState<'attestation' | 'verified-record'>('attestation');
  const [intakeReason, setIntakeReason] = useState('');
  const [intakeEvidence, setIntakeEvidence] = useState('');
  // QC form
  const [qcReason, setQcReason] = useState('');
  const [qcEvidence, setQcEvidence] = useState('');

  const [busy, setBusy] = useState<null | 'intake' | 'qc'>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    setStatus(null);
    try {
      setStatus(await navigationRelease.get(submissionId));
    } catch (err) {
      const body = parseApiErrorBody(err);
      setLoadError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleError = useCallback((err: unknown): void => {
    const body = parseApiErrorBody(err);
    const statusCode = err instanceof ApiError ? err.status : 0;
    const detail = body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err));
    setActionError(
      statusCode === 409
        ? `${detail} — refresh the release status and re-review before recording; nothing was recorded.`
        : detail,
    );
  }, []);

  const submitIntake = useCallback(async (): Promise<void> => {
    setActionError(null);
    setActionOk(null);
    if (intakeReason.trim().length === 0) {
      setActionError('A reason is required to record intake approval.');
      return;
    }
    const evidenceRef = intakeEvidence.trim();
    if (intakeKind === 'verified-record' && evidenceRef.length === 0) {
      setActionError('A verifiable intake record requires an evidence reference.');
      return;
    }
    setBusy('intake');
    try {
      await navigationRelease.intake(submissionId, {
        documentId,
        expectedArtifactSha256: sourceSha256,
        kind: intakeKind,
        reason: intakeReason.trim(),
        ...(evidenceRef.length > 0 ? { evidenceRef } : {}),
      });
      setIntakeReason('');
      setIntakeEvidence('');
      setActionOk('Intake approval recorded.');
      await load();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(null);
    }
  }, [submissionId, documentId, sourceSha256, intakeKind, intakeReason, intakeEvidence, load, handleError]);

  const compilerPackageId = status?.compilerPackageId ?? null;
  const finalArtifactSha256 = status?.finalArtifactSha256 ?? null;

  const qcBlockedReason = !canSign
    ? 'recording a publishing-QC sign-off requires Documents:sign'
    : loading || !status
      ? 'load the current release status first'
      : status.stale
        ? 'the package assessment is stale; recompile and review the current package'
      : !finalArtifactSha256
        ? 'no sealed final package to sign off — compile and seal the package first'
        : !compilerPackageId
          ? 'the exact compiled package could not be identified; refresh the status before signing off'
          : null;
  const qcEnabled = qcBlockedReason === null && busy === null;

  const submitQc = useCallback(async (): Promise<void> => {
    setActionError(null);
    setActionOk(null);
    if (!finalArtifactSha256 || !compilerPackageId) return;
    if (qcReason.trim().length === 0) {
      setActionError('A reason is required to record a publishing-QC sign-off.');
      return;
    }
    const evidenceRef = qcEvidence.trim();
    setBusy('qc');
    try {
      await navigationRelease.qc(submissionId, {
        expectedArtifactSha256: finalArtifactSha256,
        expectedCompilerPackageId: compilerPackageId,
        reason: qcReason.trim(),
        ...(evidenceRef.length > 0 ? { evidenceRef } : {}),
      });
      setQcReason('');
      setQcEvidence('');
      setActionOk('Publishing-QC sign-off recorded.');
      await load();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(null);
    }
  }, [submissionId, finalArtifactSha256, compilerPackageId, qcReason, qcEvidence, load, handleError]);

  const intakeBlockedReason = !canWrite
    ? 'recording intake approval requires Documents:write'
    : intakeKind === 'verified-record' && !canSign
      ? 'a verifiable intake record requires Documents:sign'
      : loading || !status
        ? 'load the current release status first'
        : null;
  const intakeEnabled = intakeBlockedReason === null && busy === null;

  return (
    <details style={navStyles.section} data-testid="nav-release">
      <summary style={{ ...navStyles.heading, cursor: 'pointer' }}>
        Release review — intake evidence &amp; publishing QC
        {status && <span style={{ ...navStyles.muted, fontWeight: 400 }}> · {statusLabel(status).text}</span>}
      </summary>

      <div style={{ marginTop: 8 }}>
        {loading && !status && (
          <div style={navStyles.muted} data-testid="nav-release-loading">
            Loading release status…
          </div>
        )}
        {loadError && !status && (
          <div style={navStyles.error} role="alert" data-testid="nav-release-load-error">
            Could not load release status: {loadError}
          </div>
        )}

        {status && (
          <div data-testid="nav-release-status">
            <div style={{ ...navStyles.value, color: statusLabel(status).color, fontWeight: 600 }} data-testid="nav-release-verdict">
              {statusLabel(status).text}
            </div>
            <div style={navStyles.muted} data-testid="nav-release-conformance">
              Regulatory conformance: {status.regulatoryConformance}
              {status.stale ? ` · stale: ${status.staleReason ?? 'a change since compile invalidated this verdict'}` : ''}
            </div>
            {status.status !== 'none' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px', marginTop: 4 }}>
                <span style={navStyles.label}>Final package</span>
                <span style={{ ...navStyles.value, ...navStyles.mono }} data-testid="nav-release-final-hash">{short(finalArtifactSha256)}</span>
                <span style={navStyles.label}>Compiler package</span>
                <span style={{ ...navStyles.value, ...navStyles.mono }} data-testid="nav-release-compiler-id">
                  {compilerPackageId ? short(compilerPackageId) : 'unavailable'}
                </span>
                <span style={navStyles.label}>Policy snapshot</span>
                <span style={navStyles.value}>
                  v{status.policySnapshotVersion ?? '—'}
                  {status.currentPolicyVersion != null && status.currentPolicyVersion !== status.policySnapshotVersion
                    ? ` (current org policy is v${status.currentPolicyVersion})`
                    : ''}
                </span>
              </div>
            )}
            {status.blockers && status.blockers.length > 0 && (
              <ul style={{ ...navStyles.muted, margin: '6px 0 0 16px', padding: 0 }} data-testid="nav-release-blockers">
                {status.blockers.map((b) => (
                  <li key={b.code}>
                    {b.detail} <span style={navStyles.mono}>({b.code})</span>
                  </li>
                ))}
              </ul>
            )}
            {status.qcRecords && status.qcRecords.length > 0 && (
              <div style={{ marginTop: 6 }} data-testid="nav-release-records">
                <div style={navStyles.label}>Recorded approvals</div>
                {status.qcRecords.map((r) => (
                  <div key={r.id} style={{ ...navStyles.muted, marginTop: 2 }} data-testid={`nav-release-record-${r.id}`}>
                    {r.gate === 'intake' ? 'Intake' : 'Publishing QC'} · {kindLabel(r)} · by {r.actor} · binds{' '}
                    <span style={navStyles.mono}>{short(r.boundArtifactSha256)}</span>
                    {r.evidenceRef ? ` · evidence: ${r.evidenceRef}` : ''}
                  </div>
                ))}
              </div>
            )}
            <button type="button" style={{ ...navStyles.smallBtn, marginTop: 6 }} data-testid="nav-release-refresh" disabled={loading} onClick={() => void load()}>
              Refresh status
            </button>
          </div>
        )}

        {/* ---- Intake evidence (independent, pre-upload) ---- */}
        <div style={{ ...navStyles.row, marginTop: 10 }} data-testid="nav-release-intake">
          <div style={navStyles.label}>Intake evidence (approval of the uploaded document, before it enters AutoeCTD)</div>
          <div style={{ ...navStyles.muted, marginTop: 2 }}>
            Binds to the selected document’s source bytes:{' '}
            <span style={navStyles.mono} data-testid="nav-release-intake-hash">{short(sourceSha256)}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <select
              aria-label="Intake record kind"
              data-testid="nav-release-intake-kind"
              value={intakeKind}
              style={navStyles.input}
              onChange={(e) => setIntakeKind(e.target.value === 'verified-record' ? 'verified-record' : 'attestation')}
            >
              <option value="attestation">Operator attestation</option>
              <option value="verified-record">Verified record</option>
            </select>
          </div>
          <textarea
            data-testid="nav-release-intake-reason"
            aria-label="Intake reason"
            placeholder="Reason for this intake approval (recorded in the audit trail)"
            value={intakeReason}
            rows={2}
            style={{ ...navStyles.input, width: '100%', marginTop: 6, resize: 'vertical' }}
            onChange={(e) => setIntakeReason(e.target.value)}
          />
          <input
            data-testid="nav-release-intake-evidence"
            aria-label="Intake evidence reference"
            placeholder={intakeKind === 'verified-record' ? 'Evidence reference (required for a verified record)' : 'Evidence reference (optional)'}
            value={intakeEvidence}
            style={{ ...navStyles.input, width: '100%', marginTop: 6 }}
            onChange={(e) => setIntakeEvidence(e.target.value)}
          />
          <button
            type="button"
            data-testid="nav-release-intake-submit"
            disabled={!intakeEnabled}
            aria-disabled={!intakeEnabled}
            title={intakeBlockedReason ?? undefined}
            style={!intakeEnabled ? navStyles.buttonDisabled : navStyles.button}
            onClick={() => void submitIntake()}
          >
            {busy === 'intake' ? 'Recording…' : 'Record intake approval'}
          </button>
          {intakeBlockedReason && (
            <div style={{ ...navStyles.muted, marginTop: 4 }} data-testid="nav-release-intake-blocked">
              {intakeBlockedReason}.
            </div>
          )}
        </div>

        {/* ---- Publishing QC (independent, pre-release) ---- */}
        <div style={{ ...navStyles.row }} data-testid="nav-release-qc">
          <div style={navStyles.label}>Publishing QC (sign-off on the sealed final package, before release)</div>
          <div style={{ ...navStyles.muted, marginTop: 2 }}>
            Binds to the exact sealed package bytes <span style={navStyles.mono}>{short(finalArtifactSha256)}</span> and compiler
            package <span style={navStyles.mono}>{compilerPackageId ? short(compilerPackageId) : 'not available'}</span> you reviewed.
          </div>
          <textarea
            data-testid="nav-release-qc-reason"
            aria-label="QC reason"
            placeholder="Reason for this publishing-QC sign-off"
            value={qcReason}
            rows={2}
            style={{ ...navStyles.input, width: '100%', marginTop: 6, resize: 'vertical' }}
            onChange={(e) => setQcReason(e.target.value)}
          />
          <input
            data-testid="nav-release-qc-evidence"
            aria-label="QC evidence reference"
            placeholder="Evidence reference (optional)"
            value={qcEvidence}
            style={{ ...navStyles.input, width: '100%', marginTop: 6 }}
            onChange={(e) => setQcEvidence(e.target.value)}
          />
          <button
            type="button"
            data-testid="nav-release-qc-submit"
            disabled={!qcEnabled}
            aria-disabled={!qcEnabled}
            title={qcBlockedReason ?? undefined}
            style={!qcEnabled ? navStyles.buttonDisabled : navStyles.button}
            onClick={() => void submitQc()}
          >
            {busy === 'qc' ? 'Recording…' : 'Record publishing-QC sign-off'}
          </button>
          {qcBlockedReason && (
            <div style={{ ...navStyles.muted, marginTop: 4 }} data-testid="nav-release-qc-blocked">
              {qcBlockedReason}.
            </div>
          )}
        </div>

        {actionError && (
          <div style={{ ...navStyles.error, marginTop: 6 }} role="alert" data-testid="nav-release-action-error">
            {actionError}
          </div>
        )}
        {actionOk && (
          <div style={{ ...navStyles.muted, marginTop: 6, color: 'var(--ok, #15803d)' }} data-testid="nav-release-action-ok">
            {actionOk}
          </div>
        )}
      </div>
    </details>
  );
}

export default NavigationReleaseControls;
